import { Component, DestroyRef, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ChangeDetectionStrategy, afterNextRender, inject, signal } from "@angular/core"

@Component({
  selector: "flow-segment-control",
  templateUrl: "./flow-segment-control.component.html",
  styleUrl: "./flow-segment-control.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FlowSegmentControlComponent implements OnChanges {
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef)
  private readonly destroyRef = inject(DestroyRef)

  @Input() segments: FlowSegment[] = []
  @Input() selectedValue: string = ""
  @Input() size: FlowSegmentSize = "medium"
  @Input() width: string = "fit-content"
  @Input() segmentPadding: string = "0 16px"

  @Output() segmentSelected = new EventEmitter<string>()

  public readonly indicator = signal<IndicatorStyle>({ left: 0, width: 0, visible: false })

  private rendered = false

  constructor() {
    afterNextRender(() => {
      this.rendered = true
      this.updateIndicator()
      const observer = new ResizeObserver(() => this.updateIndicator())
      observer.observe(this.host.nativeElement)
      this.destroyRef.onDestroy(() => observer.disconnect())
    })
  }

  // selectedValue can arrive after the first render (async-loaded state), and the indicator
  // is positioned imperatively, so it has to be re-measured whenever the input changes
  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.rendered) return
    if (changes["selectedValue"] || changes["segments"]) this.updateIndicator()
  }

  public selectSegment(segment: FlowSegment): void {
    if (segment.disabled || segment.value === this.selectedValue) {
      return
    }
    this.selectedValue = segment.value
    this.moveIndicatorTo(this.segments.indexOf(segment))
    this.segmentSelected.emit(segment.value)
  }

  public isSelected(segment: FlowSegment): boolean {
    return segment.value === this.selectedValue
  }

  private updateIndicator(): void {
    this.moveIndicatorTo(this.segments.findIndex((segment) => segment.value === this.selectedValue))
  }

  private moveIndicatorTo(index: number): void {
    const buttons = this.host.nativeElement.querySelectorAll<HTMLElement>(".segment")
    const target = index >= 0 ? buttons.item(index) : null
    if (!target) {
      this.indicator.set({ left: 0, width: 0, visible: false })
      return
    }
    this.indicator.set({ left: target.offsetLeft, width: target.offsetWidth, visible: true })
  }
}

export type FlowSegment = { label: string; value: string; disabled?: boolean }
export type FlowSegmentSize = "medium" | "large"
type IndicatorStyle = { left: number; width: number; visible: boolean }
